<template>
  <div class="locations-list">
    <LoaderNew v-if="isLoading" class="locations-list__floating-loading-icon" :borderWidth="3" />

    <div
      :class="{
        'locations-list__map-floating-zone': true,
        'locations-list__map-floating-zone--disabled': isLoading,
      }"
    >
      <div class="locations-list__map-section-wrap">
        <!-- Map legend -->
        <div class="map-legend locations-list__map-legend">
          <!-- Legend snippet -->
          <div class="legend-snippet map-legend__legend-snippet">
            <label class="legend-snippet__icon-wrap">
              <span
                :style="{
                  width: '20px',
                  height: '20px',
                  border: '2px rgba(45, 149, 148, 1) solid',
                  borderRadius: ['radius', 'circle'].includes(boundarySelectMode) ? '100px' : '0',
                  display: 'block',
                  background: 'rgba(45, 149, 148, 0.3)',
                }"
              ></span>
            </label>
            <div class="legend-snippet__name">destination boundary</div>
          </div>
          <!-- / Legend snippet -->
        </div>
        <!-- / Map legend -->

        <MapSection
          class="locations-list__map-section"
          v-model:isExpanded="isMapExpanded"
          v-model:isHighlighted="isMapHighlighted"
          v-click-outside="() => (isMapExpanded = false)"
          :locationsList="props.locationsList"
          ref="domRefMapSection"
          @clickOnMap="location => (mapClickLocation = location)"
          :activeSnippetIndex="activeSnippetIndex"
          @markerDragEnd="location => (draggedMarkerNewLocation = location)"
          :boundaryGeoJson="props.boundaryGeoJson"
          :boundarySelectMode="props.boundarySelectMode"
          :withinMiles="props.withinMiles"
          :mapCenter="props.mapCenter"
          @clickOnMarker="clickOnMarker"
        />
      </div>

      <ul class="locations-list__list-itself" ref="domRefLocationsListItself">
        <li
          :class="{
            'locations-list__location-input-snippet-wrap': true,
            'locations-list__location-input-snippet-wrap--with-line-on-top': calcIsSnippetTopLineVisible(index),
            'locations-list__location-input-snippet-wrap--with-line-on-bottom': calcIsSnippetBottomLineVisible(index),
            'locations-list__location-input-snippet-wrap--disabled': [elementIndexDragged, elementIndexTouchDragged].includes(index),
          }"
          v-for="(collabLocation, index) in props.locationsList"
          :key="collabLocation?.id"
          :data-index="index"
        >
          <LocationInputSnippet
            :class="{
              'locations-list__location-input-snippet': true,
              'locations-list__location-input-snippet--pointer-events-none': isDragInProgress || isTouchDragInProgress,
            }"
            ref="domRefLocationInputSnippets"
            :index="index"
            @toggleEditMode="
              isEditMode => {
                isMapExpanded = false;
                activeSnippetIndex = isEditMode ? index : -1;
              }
            "
            :collabLocation="collabLocation"
            :isSnippetDisabled="activeSnippetIndex !== -1 && activeSnippetIndex !== index"
            :isCollabLocationUsedInCollab="collabLocationsStore.usedLocationsInAdventures.includes(collabLocation.id)"
            :showValidation="showValidation"
            @googlePlaceSelected="
              place => {
                // fit the map boundaries to the locations
                domRefMapSection?.fitMapToLocations();
              }
            "
            @update:collabLocation="
              newObj => {
                const _locationsList = [...props.locationsList];
                _locationsList[index] = newObj;
                emit('update:locationsList', _locationsList);
              }
            "
            @showSetThePinNote="isSetThePinNoteVisible => (isMapHighlighted = isSetThePinNoteVisible)"
            @deleteLocation="
              () => {
                activeSnippetIndex = -1;
                deleteLocation(collabLocation.id);
              }
            "
            :saveLocationFunc="saveLocation"
          />
        </li>
      </ul>

      <!-- if any locations are used in adventures, show the warning message  -->
      <p class="global-p locations-list__changes-not-reflected-in-adventures" v-if="collabLocationsStore.usedLocationsInAdventures.length">
        <span style="font-size: 20px; color: sandybrown; vertical-align: bottom">*</span> - Changes to locations will not be reflected in the existing adventure stops.
      </p>

      <p v-if="!props.locationsList.length">No locations have been added for this visit</p>

      <SrpButton class="locations-list__add-location-btn" fill="outlined" size="small" @click="addNewLocationField" :isDisabled="activeSnippetIndex !== -1">
        <template #icon><IconEmbedded name="plus_3-5" :size="22" /></template>

        <template v-if="props.locationsList?.length">Add another location</template>
        <template v-else>Add location</template>
      </SrpButton>
    </div>

    <template v-if="props.isVisitPlanNotesVisible">
      <textarea
        :class="{
          'global-textarea': true,
          'locations-list__additional-notes-textarea': true,
          'locations-list__additional-notes-textarea--disabled': isLoading,
        }"
        placeholder="Additional Notes (optional)"
        :value="props.visitPlanNotes"
        @change="$event => emit('update:visitPlanNotes', ($event.target as HTMLTextAreaElement).value)"
        style="margin-bottom: 12px"
      ></textarea>

      <SrpButton fill="outlined" @click="saveAdditionalNotes(props.visitPlanNotes)" :isDisabled="isLoading" size="small">
        <template v-if="isLoading" #icon><LoadingIcon :size="18" /></template>
        Save Notes
      </SrpButton>
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, toRef, nextTick, provide, onMounted } from "vue";
import { useCollabLocationsStore } from "@stores/collabLocations";
import CollabLocationsRepo from "@repos/CollabLocationsRepo";

// Types
import { CollabLocation, CollabLocationsData } from "@contracts/collabLocations";
import { Location } from "@contracts/location";
import { BoundarySelectMode } from "@contracts/pages";
import { CollabInputSummary } from "@contracts/collab";

// Composables
import { AreaQuarter, useMouseDragNDrop } from "@composables/useMouseDragNDrop";
import { useTouchDragNDrop } from "@composables/useTouchDragNDrop";

// Components
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import LocationInputSnippet from "./LocationInputSnippet.vue";
import MapSection from "./MapSection.vue";
import SrpButton from "@components/ui/SrpButton.vue";
import LoaderNew from "@components/Loader/LoaderNew.vue";
import LoadingIcon from "@components/ui/LoadingIcon.vue";

const props = withDefaults(
  defineProps<{
    boundaryGeoJson?: string | null;
    locationsList: Array<CollabLocation>;
    visitPlanNotes?: string;
    isVisitPlanNotesVisible?: boolean;
    boundarySelectMode?: BoundarySelectMode | null;
    withinMiles?: number | null;
    mapCenter?: Location | null;
    showValidation?: boolean;
    customerId: string | null;
    collabInputSummary: CollabInputSummary | null;
    visitPlanSentDate: string | null;
    creatorId: string | null;
  }>(),
  {
    boundaryGeoJson: null,
    locationsList: () => [],
    visitPlanNotes: "",
    isVisitPlanNotesVisible: true,
    boundarySelectMode: null,
    withinMiles: null,
    mapCenter: null,
    customerId: null,
    collabInputSummary: null,
    visitPlanSentDate: null,
    creatorId: null,
  }
);

const emit = defineEmits<{
  (e: "update:locationsList", value: Array<CollabLocation>): void;
  (e: "update:visitPlanNotes", value: string): void;
  (e: "update:locationsData", value: CollabLocationsData): void;
}>();

// Stores
const collabLocationsStore = useCollabLocationsStore();

// DOM refs
const domRefLocationInputSnippets = ref<Array<any>>([]);

// IsLoading flag =============================================================
const isLoading = ref<boolean>(false);

// Provide map click location =================================================
const mapClickLocation = ref<Location | null>(null);
provide("mapClickLocation", mapClickLocation);

// Provide dragged marker new location ========================================
const draggedMarkerNewLocation = ref<Location | null>(null);
provide("draggedMarkerNewLocation", draggedMarkerNewLocation);

// Map events
const domRefMapSection = ref<InstanceType<typeof MapSection>>();

// Initialize drag&drop composables ===========================================
const domRefLocationsListItself = ref<HTMLElement | null>(null);

async function moveElementToOtherIndex(oldIndex: number, newIndex: number, _1: any, _2: any, areaQuarter: AreaQuarter): Promise<void> {
  if (oldIndex === newIndex) {
    return;
  }
  const movedElement = props.locationsList[oldIndex];

  let _locationsList = props.locationsList.map(i => i);

  let newIndexForBE = newIndex;

  if ([3, 4].includes(areaQuarter)) {
    newIndex++;
    newIndexForBE++;
  }

  _locationsList.splice(oldIndex, 1);
  if (newIndex > oldIndex) {
    newIndex--; // to compensate for deletion on the old index
  }

  _locationsList.splice(newIndex, 0, movedElement);

  emit("update:locationsList", _locationsList);

  await saveReorderedLocationsList(movedElement.id, newIndexForBE);
}

// Save reordered list ========================================================
async function saveReorderedLocationsList(itemId: string, newIndex: number): Promise<void> {
  isLoading.value = true;
  const newLocationsData = await CollabLocationsRepo.saveReorderedList(props.customerId, props.collabInputSummary.id, props.creatorId, itemId, newIndex);
  emit("update:locationsData", newLocationsData);
  isLoading.value = false;
}

// Init composables ===========================================================
const { elementIndexDragged, elementIndexDraggedOver, elementAreaQuarterDraggedOver, isDragInProgress } = useMouseDragNDrop(
  toRef(true),
  toRef(JSON.stringify(props.locationsList)),
  domRefLocationsListItself,
  ".locations-list__location-input-snippet-wrap",
  ".location-input-snippet__column-left",
  moveElementToOtherIndex
);

const { elementIndexTouchDragged, elementIndexTouchDraggedOver, elementAreaQuarterTouchDraggedOver, isTouchDragInProgress } = useTouchDragNDrop(
  toRef(true),
  toRef(JSON.stringify(props.locationsList)),
  domRefLocationsListItself,
  ".locations-list__location-input-snippet-wrap",
  ".location-input-snippet__column-left",
  moveElementToOtherIndex
);

// Add a new location field ===================================================
async function addNewLocationField() {
  isLoading.value = true;

  const newLocationsData = await CollabLocationsRepo.createLocation(props.customerId, props.collabInputSummary.id, props.creatorId);
  emit("update:locationsData", newLocationsData);
  await nextTick();
  await domRefLocationInputSnippets.value[domRefLocationInputSnippets.value.length - 1]?.focusAutocompleteInput();

  isLoading.value = false;
}

// Padding the Locations list =================================================
onMounted(async () => {
  if (!props.visitPlanSentDate && props.locationsList.length >= props.collabInputSummary.requestedLocationCount) {
    return;
  }

  const howManyToPad = props.collabInputSummary.requestedLocationCount - props.locationsList.length;

  if (!isNaN(howManyToPad) && howManyToPad !== 0) {
    isLoading.value = true;

    const newCollabLocationsData = await CollabLocationsRepo.createLocation(props.customerId, props.collabInputSummary.id, props.creatorId, howManyToPad);

    isLoading.value = false;

    emit("update:locationsData", newCollabLocationsData);
  }
});

// Map section params =========================================================
const isMapExpanded = ref<boolean>(false);
const isMapHighlighted = ref<boolean>(false);

// Set the index of the snippet that currently in edit mode ===================
// it's needed to highlight a corresponding marker on the map
const activeSnippetIndex = ref<number>(-1);

// Save location ==============================================================
async function saveLocation(collabLocationId: string) {
  isLoading.value = true;

  const newLocationsData = await CollabLocationsRepo.updateLocation(
    props.customerId,
    props.collabInputSummary.id,
    props.creatorId,
    props.locationsList.find(l => l.id === collabLocationId)
  );

  emit("update:locationsData", newLocationsData);

  isLoading.value = false;
}

// Delete location ============================================================
async function deleteLocation(collabLocationId: string): Promise<void> {
  isLoading.value = true;

  const newLocationsData = await CollabLocationsRepo.deleteLocation(props.customerId, props.collabInputSummary.id, props.creatorId, collabLocationId);
  emit("update:locationsData", newLocationsData);

  isLoading.value = false;
}

// Calc location snippet drag&drop line indicators visibility =================
function calcIsSnippetTopLineVisible(snippetIndex: number): boolean {
  const isDraggedOver =
    [elementIndexDraggedOver.value, elementIndexTouchDraggedOver.value].includes(snippetIndex) && ![elementIndexDragged.value, elementIndexTouchDragged.value].includes(snippetIndex);
  const draggedOverAreaQuarter = elementAreaQuarterDraggedOver.value || elementAreaQuarterTouchDraggedOver.value;

  return (isDragInProgress.value || isTouchDragInProgress.value) && isDraggedOver && [1, 2].includes(draggedOverAreaQuarter);
}
function calcIsSnippetBottomLineVisible(snippetIndex: number): boolean {
  const isDraggedOver =
    [elementIndexDraggedOver.value, elementIndexTouchDraggedOver.value].includes(snippetIndex) && ![elementIndexDragged.value, elementIndexTouchDragged.value].includes(snippetIndex);
  const draggedOverAreaQuarter = elementAreaQuarterDraggedOver.value || elementAreaQuarterTouchDraggedOver.value;

  return (isDragInProgress.value || isTouchDragInProgress.value) && isDraggedOver && [3, 4].includes(draggedOverAreaQuarter);
}

// Save additional notes ======================================================
async function saveAdditionalNotes(notes: string) {
  isLoading.value = true;

  const newLocationsData = await CollabLocationsRepo.saveAdditionalNotes(props.customerId, props.collabInputSummary.id, props.creatorId, notes);
  emit("update:locationsData", newLocationsData);

  isLoading.value = false;
}

// Click on marker ============================================================
async function clickOnMarker(value) {
  if (activeSnippetIndex.value !== -1) {
    return;
  }
  const lat = value.latLng.lat();
  const lng = value.latLng.lng();

  const targetLocationIndex = props.locationsList.findIndex(l => l.latitude === lat && l.longitude === lng);

  if (targetLocationIndex !== -1 && !isMapHighlighted.value) {
    isMapExpanded.value = false;
    domRefLocationInputSnippets.value.forEach(snippet => snippet.skipEditing());
    domRefLocationInputSnippets.value[targetLocationIndex].showFullForm();

    await nextTick();
    activeSnippetIndex.value = targetLocationIndex;
  }
}
</script>

<style scoped lang="scss">
@import "@/scss/screen-size-ranges.scss";
@import "@/scss/variables.scss";
@import "@/scss/map-legend.scss";

// Locations list =============================================================
.locations-list {
  position: relative;

  &--disabled {
    opacity: 0.5;
    pointer-events: none;
  }

  &__floating-loading-icon {
    width: 50px;
    height: 50px;
    position: fixed !important;
    z-index: 3 !important;
    inset: calc(50% - 25px) auto auto calc(50% - 25px);
  }

  &__map-floating-zone {
    margin-bottom: 20px;
    position: relative;

    &--disabled {
      opacity: 0.5;
      pointer-events: none;
    }
  }

  &__map-section-wrap {
    width: 100%;
    margin-bottom: 25px;
    border-radius: 6px;
    position: sticky;
    inset: 0 auto auto 0;
    z-index: 2;
    background: rgba(200, 200, 200, 1);
  }

  &__map-legend {
    position: absolute;
    inset: 12px auto auto 12px;
    z-index: 1;
    transform: scale(0.75);
    transform-origin: top left;
    background: rgba(255, 255, 255, 0.5);
    backdrop-filter: blur(15px);
  }

  &__map-section {
    position: relative;
    z-index: 0;
  }

  &__list-itself {
    padding: 0;
    margin: 0 0 12px;
    list-style: none;
    position: relative;
    z-index: 1;
  }

  &__changes-not-reflected-in-adventures {
    padding-left: 38px;
    margin-bottom: 30px;
  }

  &__location-input-snippet-wrap {
    position: relative;
    z-index: 0;

    &::before,
    &::after {
      content: "";
      width: 100%;
      height: 3px;
      border-radius: 100px;
      position: absolute;
      left: 0;
      background: $brand-color;
      pointer-events: none;
      opacity: 0;
    }

    &::before {
      top: -2px;
    }
    &::after {
      bottom: -1px;
    }

    &--with-line-on-top {
      &::before {
        opacity: 1;
      }
    }
    &--with-line-on-bottom {
      &::after {
        opacity: 1;
      }
    }

    &--disabled {
      opacity: 0.2;
    }
  }

  &__location-input-snippet {
    &--pointer-events-none {
      pointer-events: none;
    }
  }

  &__add-location-btn {
    margin: 0 0 25px 65px;
  }

  &__additional-notes-textarea {
    height: 70px;
    min-height: 70px;

    &--disabled {
      opacity: 0.5;
      pointer-events: none;
    }
  }
}

// desktop wide -----------------------
@media (min-width: $desktop-wide-min-width) {
}
// desktop ----------------------------
@media (min-width: $desktop-min-width) and (max-width: $desktop-max-width) {
}
// laptop -----------------------------
@media (min-width: $laptop-min-width) and (max-width: $laptop-max-width) {
}
// tablet large -----------------------
@media (min-width: $tablet-large-min-width) and (max-width: $tablet-large-max-width) {
}
// tablet -----------------------------
@media (min-width: $tablet-min-width) and (max-width: $tablet-max-width) {
}
// mobile -----------------------------
@media (max-width: $mobile-max-width) {
  .locations-list {
    &__map-section {
      margin-bottom: calc(25px + 43px);
    }

    &__add-location-btn {
      margin-left: 50px;
    }
  }
}
</style>
